import React from "react";
import Box from "@mui/material/Box";

/* -----------------------------
 * Wave paths (design system)
 * ---------------------------- */

const DEFAULT_WAVE =
  "M0,40 C160,96 380,56 560,60 C740,64 960,88 1180,64 C1340,46 1400,40 1440,36 L1440,160 L0,160 Z";

const WAVE_VARIANTS = {
  "hero-contact":
    "M0,80 C120,20 300,0 480,20 C660,40 840,88 1020,88 C1200,88 1320,64 1440,40 L1440,180 L0,180 Z",
  "contact-articles":
    "M0,32 C120,80 300,96 480,88 C660,80 840,32 1020,32 C1200,32 1320,56 1440,80 L1440,120 L0,120 Z",
  "articles-projects":
    "M0,40 C160,0 360,24 540,40 C720,56 900,72 1080,64 C1260,56 1380,32 1440,24 L1440,120 L0,120 Z",
  "projects-skills":
    "M0,36 C200,96 420,108 640,100 C860,92 1080,40 1300,36 C1380,34 1420,36 1440,40 L1440,120 L0,120 Z",
  "skills-youtube":
    "M0,44 C160,0 360,80 540,56 C720,32 900,8 1080,24 C1260,40 1380,56 1440,64 L1440,120 L0,120 Z",
  "youtube-gallery":
    "M0,36 C220,80 460,20 700,40 C940,60 1180,100 1440,84 L1440,120 L0,120 Z",
  "gallery-chef":
    "M0,32 C200,64 420,96 640,80 C860,64 1080,24 1300,28 C1380,30 1420,32 1440,36 L1440,120 L0,120 Z",
  "chef-books":
    "M0,40 C200,80 600,0 1200,40 C1320,48 1380,56 1440,64 L1440,120 L0,120 Z",
  "books-favquotes":
    "M0,36 C220,80 460,20 700,40 C940,60 1180,100 1440,84 L1440,120 L0,120 Z",
  "favquotes-mythoughts":
    "M0,36 C220,80 460,20 700,40 C940,60 1180,100 1440,84 L1440,120 L0,120 Z",
};

function getWavePath(waveProps = {}) {
  if (waveProps.pathD) return waveProps.pathD;
  if (waveProps.variant && WAVE_VARIANTS[waveProps.variant]) {
    return WAVE_VARIANTS[waveProps.variant];
  }
  return DEFAULT_WAVE;
}

/* -----------------------------
 * Section component
 * ---------------------------- */

export default function Section({
  id,
  className = "section",
  style = {},
  topColor,
  bottomColor,
  wave = true,
  waveProps = {},
  labelledBy,
  children,
}) {
  const sepHeight = waveProps.height ?? 160;
  const sepTop = waveProps.top ?? -30;

  // reserve space to avoid layout shift
  const visibleInside = Math.max(sepHeight + sepTop, 0);

  return (
    <section
      id={id}
      className={className}
      aria-labelledby={labelledBy}
      style={{
        position: "relative",
        backgroundColor: bottomColor,
        paddingTop: visibleInside || undefined,
        minHeight: wave ? visibleInside : undefined,
        ...style,
      }}
    >
      {wave && topColor && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: sepTop,
            height: sepHeight,
            pointerEvents: "none",
            backgroundColor: topColor,
            lineHeight: 0,
            zIndex: 10,
          }}
        >
          <svg
            viewBox={waveProps.viewBox ?? "0 0 1440 120"}
            preserveAspectRatio="none"
            style={{ width: "100%", height: "100%", display: "block" }}
            aria-hidden
          >
            <path
              fill={bottomColor ?? "transparent"}
              d={getWavePath(waveProps)}
            />
          </svg>
        </Box>
      )}

      {children}
    </section>
  );
}
